import React, { useEffect, useState, useRef } from "react";
// ANT DESIGN COMPONENTS
import { Table, Alert, Empty, Tag, Input, Button, Space } from "antd";
import { TablePaginationConfig } from "antd/es/table";
import { FilterDropdownProps, FilterValue, SorterResult } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetOrganizationsQuery,
  usePostOrganizationMutation,
  usePutOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
import {
  updateOrganization,
  updateFormAction,
  updateOrganizationBreadcrumb,
} from "../../../redux/organizations/organizationSlice";
import { useGetCountriesMutation } from "../../../redux/countriesStatesCitiesAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
import ModalDeleteItemOrganization from "../../components/modal/ModalDeleteItemOrganization";
import TableColumnOrganizationType from "../../components/table/TableColumnOrganizationType";
import OrganizationsTableActions from "./OrganizationsTableActions";
import FormularyRowText from "../../components/formularyRow/FormularyRowText";
import FormularyRowSelectOrganization from "../../components/formularyRow/FormularyRowSelectOrganization";
import FormularyRowSelectCountry from "../../components/formularyRow/FormularyRowSelectCountry";
import FormularyRowSelectState from "../../components/formularyRow/FormularyRowSelectState";
import FormularyRowSelectCity from "../../components/formularyRow/FormularyRowSelectCity";
import FormularyRowPhoneNumber from "../../components/formularyRow/FormularyRowPhoneNumber";
import FormularyHeaderActionsCreate from "../../components/formularyHeader/FormularyHeaderActionsCreate";
import FormularyHeaderActionsEdit from "../../components/formularyHeader/FormularyHeaderActionsEdit";
// INTERFACES
import { OrganizationInterface } from "../../../interfaces/Organizations.interface";
import { RootState } from "../../../app/store";
// CONTANTS
import { BLANK_ORGANIZATION } from "../../../constants/organization";
// FLATBUFFER DISPLAY
import { formatUuidT, orgTypeLabel } from "../../../flatbuffers/display";
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
import { FormAction } from "../../../types/enums";

type SortDirection = "asc" | "desc";

const OrganizationTable = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");
  const dropdownRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const { theme, globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [currentOrgId, setCurrentOrgId] = useState<string>("");
  const [organizations, setOrganizations] = useState<OrganizationInterface[]>([]);
  const [organization, setOrganization] = useState<OrganizationInterface>({
    ...BLANK_ORGANIZATION,
    country: "United States",
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    total: 0,
  });
  // FILTER STATES (server-side)
  const [filterName, setFilterName] = useState<string>("");
  const [filterAddress, setFilterAddress] = useState<string>("");
  const [filterCountry, setFilterCountry] = useState<string>("");
  const [filterState, setFilterState] = useState<string>("");
  const [filterCity, setFilterCity] = useState<string>("");
  const [filterPhone, setFilterPhone] = useState<string>("");
  const [filterOrgType, setFilterOrgType] = useState<string>("");
  // SORT STATE (server-side)
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const {
    organizationRefreshTable,
    tourClickActions,
    formAction,
    organization: selectedOrganization,
    organizationBreadcrumb,
    organizationSelectMultipleData,
  } = useSelector((state: RootState) => state.organization);
  const [triggerGetCountries, { data: countries }] = useGetCountriesMutation();

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetOrganizationsTable, { data, isLoading, isError, isFetching }] =
    useLazyGetOrganizationsQuery();
  const [
    triggerPostOrganization,
    {
      isSuccess: isSuccessPostOrganization,
      isLoading: isLoadingPostOrganization,
      isError: isErrorPostOrganization,
      error: errorPostOrganization,
      reset: resetPostOrganization,
    },
  ] = usePostOrganizationMutation();

  const [
    triggerPutOrganization,
    {
      isSuccess: isSuccessPutOrganization,
      isError: isErrorPutOrganization,
      error: errorPutOrganization,
      isLoading: isLoadingPutOrganization,
      reset: resetPutOrganization,
    },
  ] = usePutOrganizationMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getData = async (page: number, limit: number, orgId: string) => {
    const token = await GETJwtToken();
    triggerGetOrganizationsTable({
      page,
      limit,
      token,
      filterName,
      filterAddress,
      filterCountry,
      filterState,
      filterCity,
      filterPhone,
      filterOrgType,
      statusFilter: "ACTIVE",
      sortField,
      sortDirection,
    }).unwrap();
  };

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<OrganizationInterface> | SorterResult<OrganizationInterface>[],
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSortField =
      activeSorter && activeSorter.order ? String(activeSorter.columnKey ?? "") : "";
    const nextSortDirection: SortDirection = activeSorter?.order === "descend" ? "desc" : "asc";
    setSortField(nextSortField);
    setSortDirection(nextSortDirection);
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total,
    });
  };

  const checkFormIsValid = () => {
    if (
      organization.name === "" ||
      organization.parentId === null ||
      organization.address === "" ||
      organization.country === "" ||
      organization.state === "" ||
      organization.city === "" ||
      organization.phone === ""
    ) {
      return true;
    }
    return false;
  };

  const postOrganization = async () => {
    const token = await GETJwtToken();
    const BODY = {
      body: organization,
      token,
    };
    triggerPostOrganization(BODY);
  };

  const putOrganization = async () => {
    const token = await GETJwtToken();
    const BODY = {
      id: formatUuidT(organization.id),
      body: organization,
      token,
    };
    triggerPutOrganization(BODY);
  };

  const onClickSave = async () => {
    if (formAction === FormAction.Create) {
      postOrganization();
    } else if (formAction === FormAction.Edit) {
      putOrganization();
    }
  };

  const onClickResetForm = () => {
    dispatch(updateFormAction(FormAction.Create));
    resetPostOrganization();
    resetPutOrganization();
    setOrganization({
      ...BLANK_ORGANIZATION,
      country: "United States",
    });
    setCurrentOrgId(globalOrganizationId);
    setPagination({
      ...pagination,
      current: 1,
      pageSize: 10,
    });
    getData(1, 10, globalOrganizationId);
    dispatch(updateOrganizationBreadcrumb([]));
  };

  const onChangeFormValue = (newValue: string | number, variableName: string) => {
    if (variableName !== "") {
      const copy: OrganizationInterface = { ...organization };
      if (variableName === "name" && typeof newValue === "string") {
        copy.name = newValue;
      } else if (variableName === "address" && typeof newValue === "string") {
        copy.address = newValue;
      } else if (variableName === "state" && typeof newValue === "string" && newValue !== "") {
        copy.state = newValue;
      } else if (variableName === "city" && typeof newValue === "string" && newValue !== "") {
        copy.city = newValue;
      } else if (variableName === "country" && typeof newValue === "string" && newValue !== "") {
        copy.country = newValue;
      } else if (variableName === "phone" && typeof newValue === "string") {
        copy.phone = newValue;
      } else if (variableName === "parentId" && typeof newValue === "string") {
        // The parent picked here is the user's own org (line where this is
        // called passes globalOrganizationId). When that's the master org,
        // the new entity is a top-level ORG; when it's a sub-org, the new
        // entity is a SUBORG nested deeper. Role-based check replaces the
        // build-time env-var match so offline-bootstrapped installs (master
        // UUID created at runtime) classify correctly.
        if (!isMasterAdmin(userLogged, newValue)) {
          copy.type = OrgType.SUBORG;
        } else {
          copy.type = OrgType.ORG;
        }
        copy.parentId = null;
      }
      setOrganization(copy);
    }
  };

  const onClickEditInWizard = (record: OrganizationInterface) => {
    if (
      record.id !== null &&
      hasPermission(Permission.WizardInstallerAccess, permissionSet ?? [])
    ) {
      navigate(`/organization-management/setup-wizard?orgId=${formatUuidT(record.id)}`);
    }
  };

  // ************************************************* */
  // FILTER DROPDOWN HELPER ************************** */
  const renderColumnSearch = (
    placeholder: string,
    value: string,
    setValue: (v: string) => void,
  ) => ({
    filterDropdown: (props: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={() => props.confirm()}
          style={{ marginBottom: 8, display: "block", width: 200 }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => props.confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setValue("");
              if (props.clearFilters) props.clearFilters();
              props.confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered || value !== "" ? "#1890ff" : undefined }} />
    ),
  });

  // ************************************************* */
  // TABLE COLUMNS *********************************** */
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      ...renderColumnSearch(t("general.name") as string, filterName, setFilterName),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowText
          record={record}
          text={text}
          value={String(organization.name ?? "")}
          inputIcon
          placeHolder={
            organization.type === OrgType.ORG
              ? t("general.newOrganization")
              : t("general.newSubOrganization")
          }
          formAction={formAction}
          variableName="name"
          itemId={formatUuidT(organization.id)}
          inputValidationType="na"
          textLink
          onChangeFormValue={onChangeFormValue}
        />
      ),
    },
    {
      title: "Organization",
      dataIndex: "parentId",
      key: "parent_id",
      sorter: true,
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: any, record: OrganizationInterface) => (
        <FormularyRowSelectOrganization
          record={record}
          text={formatUuidT(text)}
          value={formatUuidT(organization.parentId)}
          inputIcon={false}
          formAction={formAction}
          variableName="parentId"
          itemId={formatUuidT(organization.id)}
          textLink={false}
          onChangeFormValue={onChangeFormValue}
        />
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      sorter: true,
      ...renderColumnSearch(t("general.address") as string, filterAddress, setFilterAddress),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowText
          record={record}
          text={text}
          value={String(organization.address ?? "")}
          inputIcon={false}
          placeHolder={t("general.address")}
          formAction={formAction}
          variableName="address"
          itemId={formatUuidT(organization.id)}
          inputValidationType="na"
          textLink={false}
          onChangeFormValue={onChangeFormValue}
        />
      ),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: true,
      ...renderColumnSearch(t("general.country") as string, filterCountry, setFilterCountry),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowSelectCountry
          record={record}
          text={text}
          value={String(organization.country ?? "")}
          inputIcon={false}
          formAction={formAction}
          itemId={formatUuidT(organization.id)}
          textLink={false}
          onChangeFormValue={onChangeFormValue}
          countries={countries}
        />
      ),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      sorter: true,
      ...renderColumnSearch(t("general.state") as string, filterState, setFilterState),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowSelectState
          record={record}
          text={text}
          value={String(organization.state ?? "")}
          inputIcon={false}
          formAction={formAction}
          itemId={formatUuidT(organization.id)}
          textLink={false}
          onChangeFormValue={onChangeFormValue}
          country={String(organization.country ?? "")}
        />
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      sorter: true,
      ...renderColumnSearch(t("general.city") as string, filterCity, setFilterCity),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowSelectCity
          record={record}
          text={text}
          value={String(organization.city ?? "")}
          inputIcon={false}
          formAction={formAction}
          itemId={formatUuidT(organization.id)}
          textLink={false}
          onChangeFormValue={onChangeFormValue}
          country={String(organization.country ?? "")}
          countryState={String(organization.state ?? "")}
        />
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      sorter: true,
      ...renderColumnSearch(t("general.phoneNumber") as string, filterPhone, setFilterPhone),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: string, record: OrganizationInterface) => (
        <FormularyRowPhoneNumber
          record={record}
          text={text}
          value={String(organization.phone ?? "")}
          formAction={formAction}
          itemId={formatUuidT(organization.id)}
          textLink={false}
          onChangeFormValue={onChangeFormValue}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      ...renderColumnSearch(t("general.type") as string, filterOrgType, setFilterOrgType),
      onCell: (record: OrganizationInterface) => ({
        onClick: () => {
          onClickEditInWizard(record);
        },
      }),
      render: (text: OrgType, record: OrganizationInterface) => (
        <>
          {record.id === null ? (
            <TableColumnOrganizationType type={orgTypeLabel(organization.type)} />
          ) : (
            <TableColumnOrganizationType type={orgTypeLabel(text)} />
          )}
        </>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actioins",
      key: "actions",
      render: (_: any, record: OrganizationInterface) => (
        <>
          {record.id === null ? (
            <FormularyHeaderActionsCreate
              formAction={formAction}
              formIsValid={checkFormIsValid()}
              onClickSave={onClickSave}
              onClickResetForm={onClickResetForm}
            />
          ) : (
            <>
              {formAction === FormAction.Edit &&
              formatUuidT(record.id) === formatUuidT(organization.id) ? (
                <FormularyHeaderActionsEdit
                  formIsValid={checkFormIsValid()}
                  onClickSave={onClickSave}
                  onClickResetForm={onClickResetForm}
                />
              ) : (
                <OrganizationsTableActions selectedItem={record} />
              )}
            </>
          )}
        </>
      ),
    },
  ];

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    triggerGetCountries({});
  }, []);

  useEffect(() => {
    if (data && data.data) {
      setPagination({
        ...pagination,
        total: data.total,
      });

      // Pin orgs referenced by the current monitoring grid to the top.
      const monitoringOrgIds = new Set<string>(
        organizationSelectMultipleData
          .map((entry: any) => entry.idOrganization as string)
          .filter(Boolean),
      );

      if (monitoringOrgIds.size === 0) {
        setOrganizations(data.data);
      } else {
        const pinned: OrganizationInterface[] = [];
        const rest: OrganizationInterface[] = [];
        const seenPinned = new Set<string>();
        (data.data as OrganizationInterface[]).forEach((org) => {
          const id = formatUuidT(org.id);
          if (id && monitoringOrgIds.has(id) && !seenPinned.has(id)) {
            pinned.push(org);
            seenPinned.add(id);
          } else {
            rest.push(org);
          }
        });
        setOrganizations([...pinned, ...rest]);
      }
    }
  }, [data, organizationSelectMultipleData]);

  useEffect(() => {
    if (tourClickActions) {
      if (dropdownRefs.current[0]) {
        dispatch(updateOrganization(organizations[0]));
        dropdownRefs.current[0].click();
      }
    }
  }, [tourClickActions]);

  useEffect(() => {
    if (organizationRefreshTable) {
      onClickResetForm();
    }
  }, [organizationRefreshTable]);

  useEffect(() => {
    if (
      isSuccessPostOrganization ||
      isSuccessPutOrganization ||
      isErrorPostOrganization ||
      isErrorPutOrganization
    ) {
      onClickResetForm();
    }
  }, [
    isSuccessPostOrganization,
    isSuccessPutOrganization,
    isErrorPostOrganization,
    isErrorPutOrganization,
  ]);

  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id !== null) {
      setOrganization({ ...selectedOrganization });
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (organizationBreadcrumb.length > 0 && globalOrganizationId !== "") {
      getData(1, 10, organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId);
      setCurrentOrgId(organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId);
      onChangeFormValue(
        organizationBreadcrumb[organizationBreadcrumb.length - 1].orgId,
        "parentId",
      );
    }
  }, [organizationBreadcrumb]);

  useEffect(() => {
    if (globalOrganizationId !== "") {
      getData(1, 10, globalOrganizationId);
      setCurrentOrgId(globalOrganizationId);
      onChangeFormValue(globalOrganizationId, "parentId");
      dispatch(updateOrganizationBreadcrumb([]));
    }
  }, [globalOrganizationId]);

  // RELOAD on filter / sort / pagination change
  useEffect(() => {
    if (currentOrgId !== "") {
      getData(pagination.current || 1, pagination.pageSize || 10, currentOrgId);
    }
  }, [
    filterName,
    filterAddress,
    filterCountry,
    filterState,
    filterCity,
    filterPhone,
    filterOrgType,
    sortField,
    sortDirection,
    pagination.current,
    pagination.pageSize,
  ]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="mt-3">
      {hasPermission(Permission.OrgsView, permissionSet ?? []) ? (
        <>
          {!isError ? (
            <Table
              rowClassName={(record, index) => {
                if (
                  (index === 0 && formAction === FormAction.Create) ||
                  (formAction === FormAction.Edit &&
                    formatUuidT(record.id) === formatUuidT(organization.id))
                ) {
                  return "table-row-active";
                }
                return getTableRowClass(index, theme);
              }}
              className="my-custom-table"
              dataSource={organizations}
              loading={
                isLoading || isFetching || isLoadingPostOrganization || isLoadingPutOrganization
              }
              rowKey={(record) => formatUuidT(record.id)}
              size="small"
              pagination={pagination}
              onChange={handleTableChange}
              footer={() => (
                <div className="generalStyles__flexEnd">
                  <Tag>{pagination.total} Rows</Tag>
                </div>
              )}
              scroll={{ x: 900 }}
              columns={columns}
            />
          ) : (
            <Alert
              message={t("organizationManagement.errorDescription.error")}
              description={t("organizationManagement.errorDescription.text1")}
              type="error"
              showIcon
              className="mt-3"
            />
          )}
        </>
      ) : (
        <>
          <div className="generalStyles__noAccessToListTable">
            <Empty
              description={t("organizationManagement.listNotAvailableOrganizations")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        </>
      )}
      {/** DELETE ITEM COMPONENT */}
      <ModalDeleteItemOrganization
        nameComponent="organization"
        nameItem={String(selectedOrganization.name ?? "")}
        id={formatUuidT(selectedOrganization.id)}
      />
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostOrganization}
        isSuccess={isSuccessPostOrganization}
        requestType="POST"
        error={errorPostOrganization}
        name="Organization"
      />
      <GlobalAlert2
        isError={isErrorPutOrganization}
        isSuccess={isSuccessPutOrganization}
        requestType="PUT"
        error={errorPutOrganization}
        name="Organization"
      />
    </div>
  );
};

export default OrganizationTable;
